import { useEffect, useMemo, useState } from 'react';
import {
  MarkdownCollabContentAdapter,
  asTeamDocumentId,
  asTeamProjectId,
} from '@nimbalyst/collab-bundle/editor';
import { Doc as YDoc } from 'yjs';
import {
  getTeamSessionForOrg,
  type BrowserSessionClient,
  type TeamMemberId,
  type TeamSession,
} from '../auth/teamJwt';
import { CollabEditorMount } from '../collab/CollabEditorMount';
import { EDITOR_LOADING, HARNESS_PAGE } from '../collab/editorSurfaceStyles';
import { EYEBROW } from '../controlStyles';
import { ExtensionEditorMount } from '../collab/ExtensionEditorMount';
import {
  getBrowserCollabCodec,
  loadBrowserExtension,
} from '@nimbalyst/web-editors/registry';
import {
  PINNED_CSV_EXTENSION_MANIFEST,
  registerPinnedBrowserExtensions,
} from '../extensions/pinnedBrowserExtensions';
import {
  BUILTIN_CANVAS_COMPONENT,
  BUILTIN_CANVAS_EXTENSION_ID,
} from '../canvas/builtinCanvasExtension';
import {
  clearWebCanvasCardScope,
  setWebCanvasCardScope,
} from '../canvas/webCanvasCardScope';

function base64UrlJson(value: unknown): string {
  return btoa(JSON.stringify(value)).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}

function localVerificationJwt(orgId: string, memberId: string): string {
  return `${base64UrlJson({ alg: 'none', typ: 'JWT' })}.${base64UrlJson({
    sub: memberId,
    exp: Math.floor(Date.now() / 1000) + 3_600,
    'https://stytch.com/organization': { organization_id: orgId },
  })}.local-verification`;
}

function useLocalVerificationSession(orgId: string, memberId: string): TeamSession | null {
  const [teamSession, setTeamSession] = useState<TeamSession | null>(null);
  const sessionClient = useMemo<BrowserSessionClient>(() => {
    const jwt = localVerificationJwt(orgId, memberId);
    return {
      getTokens: () => ({ session_jwt: jwt }),
      authenticate: async () => ({ session_jwt: jwt }),
      exchange: async () => ({ session_jwt: jwt }),
    };
  }, [memberId, orgId]);

  useEffect(() => {
    let active = true;
    void getTeamSessionForOrg(sessionClient, orgId, 60, memberId).then((session) => {
      if (active) setTeamSession(session);
    });
    return () => { active = false; };
  }, [memberId, orgId, sessionClient]);

  return teamSession;
}

function LiveCollabHarness({
  serverUrl,
  orgId,
  projectId,
  documentId,
  memberId,
}: {
  serverUrl: string;
  orgId: string;
  projectId: string;
  documentId: string;
  memberId: string;
}) {
  const teamSession = useLocalVerificationSession(orgId, memberId);

  const source = useMemo(() => teamSession ? ({
    kind: 'team-room' as const,
    serverUrl,
    room: {
      orgId: teamSession.orgId,
      projectId: asTeamProjectId(projectId),
      documentId: asTeamDocumentId(documentId),
    },
    auth: {
      scope: 'team' as const,
      memberId: teamSession.memberId,
      getTeamJwt: async () => teamSession.jwt,
    },
  }) : null, [documentId, projectId, serverUrl, teamSession]);

  if (!source) return <div className={EDITOR_LOADING}>Preparing local verification identity…</div>;
  const desktopHref = `nimbalyst://doc/${encodeURIComponent(documentId)}?orgId=${encodeURIComponent(orgId)}`;
  return (
    <CollabEditorMount
      source={source}
      user={{ memberId: source.auth.memberId, name: memberId }}
      desktopHref={desktopHref}
    />
  );
}

const HARNESS_CSV = [
  'Region,Units,Revenue',
  'North,120,4800',
  'South,86,3440',
  'East,204,8160',
].join('\n');

/**
 * The same proof for an extension-provided editor: a warm Y.Doc, seeded through
 * the extension's own codec, painted by the extension's own component on the
 * browser `EditorHost`.
 *
 * It imports the real pinned bundle rather than a stub, so it exercises the one
 * thing no unit test can: RevoGrid registering its custom element once inside
 * this page and rendering a grid from a `Y.Text`.
 */
function ExtensionEditorHarness() {
  const [document, setDocument] = useState<YDoc | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    registerPinnedBrowserExtensions();
    let active = true;
    const nextDocument = new YDoc();
    void loadBrowserExtension(PINNED_CSV_EXTENSION_MANIFEST.id).then(() => {
      // Seeded through the codec the bundle registered at activation, so the
      // harness cannot paint from a Y.Doc layout the extension would not write.
      const codec = getBrowserCollabCodec('csv');
      if (!codec) throw new Error('The pinned spreadsheet registered no CSV codec.');
      codec.seedFromFile(nextDocument, HARNESS_CSV);
      if (!active) {
        nextDocument.destroy();
        return;
      }
      setDocument(nextDocument);
    }).catch((cause: unknown) => {
      nextDocument.destroy();
      if (active) setFailure(cause instanceof Error ? cause.message : String(cause));
    });
    return () => { active = false; };
  }, []);

  useEffect(() => () => document?.destroy(), [document]);

  if (failure) return <div className={EDITOR_LOADING}>{failure}</div>;
  if (!document) return <div className={EDITOR_LOADING}>Loading the pinned spreadsheet…</div>;
  return (
    <ExtensionEditorMount
      source={{ kind: 'in-memory', document }}
      user={{ memberId: 'browser-harness' as TeamMemberId, name: 'Browser harness' }}
      fileName="harness.csv"
      extensionId={PINNED_CSV_EXTENSION_MANIFEST.id}
      componentName="SpreadsheetEditor"
      permissions={PINNED_CSV_EXTENSION_MANIFEST.permissions}
    />
  );
}

/**
 * The board, in a real browser, over the real card seam.
 *
 * Deliberately mixes the three things a canvas card can be in this host, so one
 * page shows all of them at once: native cards the canvas draws itself, a
 * workspace `file` card that a browser cannot open and must therefore explain,
 * and a shared `doc` card that mounts a live editor in its own room.
 *
 * The `doc` cards only resolve when the live query parameters are present,
 * because a card names an org and a document but never a project -- so the
 * project scope has to be published, exactly as `useProjectDocsScope` publishes
 * it in the console proper.
 */
function CanvasEditorHarness({
  live,
}: {
  live: { orgId: string; projectId: string; documentId: string; memberId: string } | null;
}) {
  const [document, setDocument] = useState<YDoc | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const teamSession = useLocalVerificationSession(live?.orgId ?? 'org-harness', live?.memberId ?? 'harness');

  const board = useMemo(() => JSON.stringify({
    nodes: [
      {
        id: 'note',
        type: 'text',
        x: 40, y: 40, width: 300, height: 140,
        text: '## Project Canvas\n\nNative cards, a local file card, and a shared document card.',
      },
      {
        id: 'local-file',
        type: 'file',
        x: 40, y: 210, width: 300, height: 180,
        file: 'design/pricing.calc',
        'x-nimbalyst': { label: 'pricing.calc' },
      },
      {
        id: 'other-org',
        type: 'file',
        x: 40, y: 420, width: 300, height: 160,
        file: 'shared.md',
        'x-nimbalyst': {
          label: 'A neighbouring org',
          reference: { kind: 'doc', uri: 'nimbalyst://doc/org-elsewhere/document-elsewhere' },
        },
      },
      {
        id: 'shared-doc',
        type: 'file',
        x: 390, y: 40, width: 520, height: 540,
        file: 'notes.md',
        'x-nimbalyst': {
          label: 'Shared note',
          reference: {
            kind: 'doc',
            uri: `nimbalyst://doc/${live?.orgId ?? 'org-harness'}/${live?.documentId ?? 'document-harness'}`,
          },
        },
      },
    ],
    edges: [
      { id: 'note-to-doc', fromNode: 'note', fromSide: 'right', toNode: 'shared-doc', toSide: 'left' },
    ],
  }), [live?.documentId, live?.orgId]);

  useEffect(() => {
    if (!live || !teamSession) return;
    const scope = {
      orgId: live.orgId,
      projectId: live.projectId,
      teamSession,
      getTeamJwt: async () => teamSession.jwt,
    };
    setWebCanvasCardScope(scope);
    return () => clearWebCanvasCardScope(scope);
  }, [live, teamSession]);

  useEffect(() => {
    registerPinnedBrowserExtensions();
    let active = true;
    const nextDocument = new YDoc();
    void loadBrowserExtension(BUILTIN_CANVAS_EXTENSION_ID).then(() => {
      // Seeded through the codec the canvas module itself registers, so the
      // harness cannot paint from a Y.Doc layout the binding would not write.
      const codec = getBrowserCollabCodec('canvas');
      if (!codec) throw new Error('The canvas bundle registered no codec.');
      codec.seedFromFile(nextDocument, board);
      if (!active) {
        nextDocument.destroy();
        return;
      }
      setDocument(nextDocument);
    }).catch((cause: unknown) => {
      nextDocument.destroy();
      if (active) setFailure(cause instanceof Error ? cause.message : String(cause));
    });
    return () => { active = false; };
  }, [board]);

  useEffect(() => () => document?.destroy(), [document]);

  if (failure) return <div className={EDITOR_LOADING}>{failure}</div>;
  if (!document) return <div className={EDITOR_LOADING}>Loading Project Canvas…</div>;
  return (
    // A board fills the height it is given rather than growing to its content,
    // and inside a zero-height box every card measures 0x0 -- which the LOD
    // model reads as "not visible" and holds the whole board cold. The docs
    // route gives the editor a real height; this harness page has to say so.
    <div className="mx-auto h-[70vh] w-full max-w-[80rem] [&>.collab-editor-surface]:h-full">
      <ExtensionEditorMount
        source={{ kind: 'in-memory', document }}
        user={{ memberId: 'browser-harness' as TeamMemberId, name: 'Browser harness' }}
        fileName="harness.canvas"
        extensionId={BUILTIN_CANVAS_EXTENSION_ID}
        componentName={BUILTIN_CANVAS_COMPONENT}
      />
    </div>
  );
}

/** Development-only W0 route proving a warm caller-owned Y.Doc paints in-page. */
export function CollabEditorHarnessRoute() {
  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  /** `?editor=extension` swaps Lexical for the pinned extension editor. */
  const extensionHarness = query.get('editor') === 'extension';
  /** `?editor=canvas` mounts Project Canvas over the browser card seam. */
  const canvasHarness = query.get('editor') === 'canvas';
  const liveConfig = useMemo(() => {
    const serverUrl = query.get('serverUrl');
    const orgId = query.get('orgId');
    const projectId = query.get('projectId');
    const documentId = query.get('documentId');
    const memberId = query.get('memberId');
    return serverUrl && orgId && projectId && documentId && memberId
      ? { serverUrl, orgId, projectId, documentId, memberId }
      : null;
  }, [query]);
  const document = useMemo(() => {
    const nextDocument = new YDoc();
    MarkdownCollabContentAdapter.seedFromFile(
      nextDocument,
      '# Browser collaboration harness\n\nThis content existed before the editor mounted.',
    );
    return nextDocument;
  }, []);
  const source = useMemo(() => ({ kind: 'in-memory' as const, document }), [document]);
  const user = useMemo(() => ({
    memberId: 'browser-harness' as TeamMemberId,
    name: 'Browser harness',
  }), []);

  useEffect(() => () => document.destroy(), [document]);

  return (
    <main className={`${HARNESS_PAGE} [&>header]:mx-auto [&>header]:mb-4 [&>header]:max-w-[80rem] [&>.collab-editor-surface]:mx-auto [&>.collab-editor-surface]:max-w-[80rem]`}>
      <header>
        <span className={EYEBROW}>
          {liveConfig ? 'Phase W2 local verification' : 'Phase W0 verification'}
        </span>
        <h1>
          {canvasHarness
            ? 'Project Canvas harness'
            : liveConfig
              ? `Collaborative editor: ${liveConfig.memberId}`
              : extensionHarness
                ? 'Extension editor harness'
                : 'Collaborative editor harness'}
        </h1>
      </header>
      {canvasHarness
        ? <CanvasEditorHarness live={liveConfig} />
        : liveConfig
          ? <LiveCollabHarness {...liveConfig} />
          : extensionHarness
            ? <ExtensionEditorHarness />
            : <CollabEditorMount source={source} user={user} />}
    </main>
  );
}
